import {
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  Theme,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useMemo } from "react";
import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import { SkuKnowledgeLevelRequest } from "services/KnowfullyAdminClient";
import { SkuFormProps } from "types/sku-related-types";
import translateError from "utils/translateError";
import SelectKnowledgeLevel from "./SelectKnowledgeLevel";
import SelectProfession from "components/SelectProfession";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  subtitle: {
    margin: theme.spacing(3, 0, 2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  checkboxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

type SkuKnowledgeLevelFormProps = {
  onCancel?: () => void;
  onSkuKnowledgeLevelCreated: () => void;
};

type SkuKnowledgeLevelFormValues = {
  skuId: string;
  knowledgeLevelId: string;
  professionId?: string;
};

const { Form } = withTypes<SkuKnowledgeLevelFormValues>();

const SkuKnowledgeLevelForm = ({
  sku,
  onCancel,
  onSkuKnowledgeLevelCreated,
  onSkuUpdated,
  ...rest
}: SkuKnowledgeLevelFormProps & SkuFormProps) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { registerPromise } = useTrashablePromises();
  const { skuService } = useGlobalContext();
  const queryClient = useQueryClient();

  const init = useMemo<SkuKnowledgeLevelFormValues>(
    () => ({
      skuId: sku?.id!,
      knowledgeLevelId: "",
    }),
    [sku]
  );

  return useMemo(() => {
    const onSubmit = async (values: SkuKnowledgeLevelFormValues) => {
      try {
        const response = await registerPromise(
          skuService.saveSkuKnowledgeLevel(
            sku.id!,
            SkuKnowledgeLevelRequest.fromJS(values)
          )
        );

        queryClient.invalidateQueries(["skuKnowledgeLevels", sku.id!]);

        onSkuKnowledgeLevelCreated();
        onSkuUpdated && onSkuUpdated(sku.id!);

        return response;
      } catch (err: any) {
        console.log(err);
        return { [FORM_ERROR]: translateError(err, t) };
      }
    };

    return (
      <Form
        onSubmit={onSubmit}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent dividers>
              <Grid container spacing={1}>
                <Field name="knowledgeLevelId" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectKnowledgeLevel
                          label={t({
                            id: "skuKnowledgeLevelTitle",
                          })}
                          {...input}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                          required
                          {...rest}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                <Field name="professionId">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectProfession
                          label={t({
                            id: "professionLabel",
                          })}
                          {...input}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button
                disabled={submitting}
                onClick={onCancel}
                data-cy="cancelButton"
              >
                {t({ id: "cancelButton" })}
              </Button>

              <Button
                type="submit"
                color="primary"
                disabled={submitting}
                data-cy="submitButton"
              >
                {t({ id: "skuKnowledgeLevelSaveButtonLabel" })}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    );
  }, [
    styles.feedback,
    init,
    onCancel,
    onSkuKnowledgeLevelCreated,
    onSkuUpdated,
    queryClient,
    registerPromise,
    rest,
    sku.id,
    skuService,
    t,
  ]);
};

export default SkuKnowledgeLevelForm;
